"use client";

import * as React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";


declare const wvcClient: any;

function SubproductoConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-16 lg:py-24 animate-pulse">
        <div className="h-10 bg-muted rounded-md w-2/3 md:w-1/3 mb-4"></div>
        <div className="h-4 bg-muted rounded-md w-1/2 md:w-1/4 mb-12"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-[4/3] bg-muted rounded-xl"></div>
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded-md w-full"></div>
            <div className="h-4 bg-muted rounded-md w-full"></div>
            <div className="h-4 bg-muted rounded-md w-5/6"></div>
            <div className="h-4 bg-muted rounded-md w-4/5"></div>
            <div className="h-32 bg-muted rounded-xl w-full mt-12"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24 text-center flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold font-default text-foreground mb-4">Producto no encontrado</h2>
        <p className="text-muted-foreground mb-8">Lo sentimos, no pudimos cargar la información de este producto.</p>
        <Button asChild>
          <Link to="/">Volver al inicio</Link>
        </Button>
      </div>
    );
  }

  const post = posts[0];
  const nombre = post.customFields?.nombre || post.title;
  const descripcion = post.customFields?.descripcion || post.content || "";
  const categoria = post.customFields?.product_category || "Catálogo";

  
  const imagen = {
    src: post.customFields?.imagen || post.featuredImage,
    srcset: "",
    sizes: ""
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16 lg:py-24">
      {}
      <div className="mb-8 md:mb-12">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
          {nombre}
        </h1>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Inicio</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="#">{categoria}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{nombre}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-16">
        {}
        <div className="w-full">
          <AspectRatio ratio={4/3} className="bg-muted rounded-xl overflow-hidden shadow-md border border-border/50">
            {imagen.src ? (
              <img
                src={imagen.src}
                data-wvc-srcset={imagen.srcset}
                data-wvc-sizes={imagen.sizes}
                alt={nombre}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-accent/10">
                Imagen no disponible
              </div>
            )}
          </AspectRatio>
        </div>

        {}
        <div className="flex flex-col h-full">
          <div className="prose prose-slate dark:prose-invert max-w-none mb-10 text-muted-foreground whitespace-pre-wrap text-base md:text-lg leading-relaxed">
            {descripcion}
          </div>

          {}
          <Card className="mt-auto bg-primary/5 border-primary/20 shadow-sm">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold font-default text-foreground">
                  ¿Te interesa este producto?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Solicita una cotización o asesoría técnica especializada.
                </p>
              </div>
              <Button size="lg" asChild className="w-full sm:w-auto shrink-0 shadow-md">
                <Link to="#">Solicitar Cotización</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator className="my-12 md:my-16" />

      {}
      <div className="text-center flex flex-col items-center justify-center space-y-6">
        <h3 className="text-2xl font-semibold font-default text-foreground">
          Explora más opciones
        </h3>
        <Button variant="outline" size="lg" asChild className="shadow-sm">
          <Link to="#">Volver a {categoria}</Link>
        </Button>
      </div>
    </div>
  );
}

export default function SingleSubproductoTemplateSection() {
  
  const current_wp_query_params = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'subproducto' };
    
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-subproducto-template-section" className="bg-background w-full">
      <PostsProvider wp_query={wp_query}>
        <SubproductoConsumer />
      </PostsProvider>
    </section>
  );
}